import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
  getDeclaration,
  getBranchAllocations,
  getIsLoading,
  getIsLoadingAppendix,
  getSaveErrors,
  getSaveErrorFields,
  getAppendixSaveErrors,
  getAppendixSaveErrorFields,
  getSelectOptions as getDeclarationSelectOptions,
  fetchDeclaration,
  fetchAppendix,
  updateDeclaration,
  updateAppendix,
  resetDeclaration,
  changeTab,
  clearSaveErrors,
  clearAppendixSaveErrors,
  downloadDeclaration
} from 'src/reducers/reports/incomeTaxDeclarations';

import { getSelectOptions } from 'src/reducers/selectOptions';

import DeclarationShowPage from '../components/DeclarationShowPage';
import ResetDeclarationModal from '../components/ResetDeclarationModal';

const getProvinces = state => getSelectOptions(state, 'provinces');
const getSubdistricts = state => (state.selectOptions && state.selectOptions.subdistricts) || {};
const getRegionalTaxOffices = state => (state.selectOptions && state.selectOptions.regionalTaxOffices) || {};
const getTaxOffices = state => (state.selectOptions && state.selectOptions.taxOffices) || {};

const DeclarationShowContainer = ({ match }) => {
  const dispatch = useDispatch();
  const id = match && match.params && match.params.id;
  const tab = match && match.params && match.params.tab;
  const isAppendixTab = tab === 'appendix';
  const activeTab = isAppendixTab ? 'appendix' : 'main';

  const declaration = useSelector(getDeclaration);
  const branchAllocations = useSelector(getBranchAllocations);
  const isLoading = useSelector(getIsLoading);
  const isLoadingAppendix = useSelector(getIsLoadingAppendix);
  const declarationSelectOptions = useSelector(getDeclarationSelectOptions);
  const saveErrors = useSelector(getSaveErrors);
  const saveErrorFields = useSelector(getSaveErrorFields);
  const appendixSaveErrors = useSelector(getAppendixSaveErrors);
  const appendixSaveErrorFields = useSelector(getAppendixSaveErrorFields);

  const provinces = useSelector(getProvinces);
  const subdistricts = useSelector(getSubdistricts);
  const regionalTaxOffices = useSelector(getRegionalTaxOffices);
  const taxOffices = useSelector(getTaxOffices);

  const dependentBranches = (declarationSelectOptions && declarationSelectOptions.dependentBranches) || [];

  useEffect(
    () => {
      if (!id) return;
      if (isAppendixTab) {
        dispatch(fetchAppendix(id));
      } else {
        dispatch(fetchDeclaration(id));
      }
    },
    [dispatch, id]
  );

  const toNumber = value => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return Number.isNaN(num) ? 0 : Math.round(num);
  };

  const handleSaveMain = async draft => {
    if (!id || !draft) return false;
    const params = {
      ct16_total_employees: toNumber(draft.ct16TotalEmployees),
      ct17_resident_employees: toNumber(draft.ct17ResidentEmployees),
      ct19_resident_with_tax: toNumber(draft.ct19ResidentWithTax),
      ct20_non_resident_with_tax: toNumber(draft.ct20NonResidentWithTax),
      ct22_resident_taxable_income: toNumber(draft.ct22ResidentTaxableIncome),
      ct23_non_resident_taxable_income: toNumber(draft.ct23NonResidentTaxableIncome),
      ct24_insurance_premium: toNumber(draft.ct24InsurancePremium),
      ct25_oil_contract_exemption: toNumber(draft.ct25OilContractExemption),
      ct27_resident_taxable_subject_to_withholding: toNumber(draft.ct27ResidentTaxableSubjectToWithholding),
      ct28_non_resident_taxable_subject_to_withholding: toNumber(draft.ct28NonResidentTaxableSubjectToWithholding),
      ct30_resident_tax: toNumber(draft.ct30ResidentTax),
      ct31_non_resident_tax: toNumber(draft.ct31NonResidentTax)
    };

    return dispatch(updateDeclaration(id, params));
  };

  const handleSaveAppendix = async allocationsDraft => {
    if (!id || !allocationsDraft || allocationsDraft.length === 0) return false;

    const params = {
      branch_allocations: allocationsDraft.map(alloc => {
        const allocParams = {
          branch_name: alloc.branchName,
          province_id: alloc.provinceId,
          province_name: alloc.provinceName,
          district_id: alloc.districtId,
          district_name: alloc.districtName,
          tax_office_code: alloc.taxOfficeCode,
          tax_office_name: alloc.taxOfficeName,
          ct10_taxable_income: toNumber(alloc.ct10TaxableIncome),
          ct11_taxable_income_subject_to_wh: toNumber(alloc.ct11TaxableIncomeSubjectToWh),
          ct12_employee_count: toNumber(alloc.ct12EmployeeCount),
          ct13_employees_with_tax: toNumber(alloc.ct13EmployeesWithTax),
          ct14_tax_withheld: toNumber(alloc.ct14TaxWithheld)
        };

        if (alloc.isNew) {
          allocParams.client_branch_id = alloc.clientBranchId;
        } else {
          allocParams.id = alloc.id;
        }

        if (alloc.destroy) {
          allocParams.is_destroy = true;
        }

        return allocParams;
      })
    };

    return dispatch(updateAppendix(id, params));
  };

  const handleTabChange = newTab => {
    if (id) {
      dispatch(changeTab(id, newTab));
    }
  };

  const handleDownload = async (declarationId, format) => {
    const name = declaration ? declaration.declarationName : '';
    return dispatch(downloadDeclaration(declarationId, format, name));
  };

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const handleOpenResetModal = useCallback(() => {
    setIsResetModalOpen(true);
  }, []);

  const handleCloseResetModal = useCallback(() => {
    setIsResetModalOpen(false);
  }, []);

  const handleReset = useCallback(
    async () => {
      if (!id) return;
      const result = await dispatch(resetDeclaration(id));
      if (result && result.ok) {
        setIsResetModalOpen(false);
        if (isAppendixTab) {
          dispatch(fetchAppendix(id));
        }
      }
    },
    [dispatch, id, isAppendixTab]
  );

  return (
    <>
      <DeclarationShowPage
        declaration={declaration}
        branchAllocations={branchAllocations}
        activeTab={activeTab}
        isLoading={isLoading}
        isLoadingAppendix={isLoadingAppendix}
        saveErrors={saveErrors}
        saveErrorFields={saveErrorFields}
        appendixSaveErrors={appendixSaveErrors}
        appendixSaveErrorFields={appendixSaveErrorFields}
        onSaveMain={handleSaveMain}
        onSaveAppendix={handleSaveAppendix}
        onTabChange={handleTabChange}
        onClearErrors={() => dispatch(clearSaveErrors())}
        onClearAppendixErrors={() => dispatch(clearAppendixSaveErrors())}
        onReset={handleOpenResetModal}
        onDownload={handleDownload}
        dependentBranches={dependentBranches}
        provinces={provinces}
        subdistricts={subdistricts}
        regionalTaxOffices={regionalTaxOffices}
        taxOffices={taxOffices}
      />
      <ResetDeclarationModal isOpen={isResetModalOpen} hideModal={handleCloseResetModal} onSubmit={handleReset} />
    </>
  );
};

DeclarationShowContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      tab: PropTypes.string
    })
  }).isRequired
};

export default DeclarationShowContainer;
